import { useState, useEffect, useMemo } from 'react';
import { client } from '@/api/client';
import { toast } from 'sonner';
import { X, Plus, Trash2, Download, Loader2, Lock, FileText, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const CATEGORIES = [
  'Truck Payment',
  'Insurance',
  'Equipment Rental',
  'Fuel Advance',
  'Personal Advance',
  'Bank Fee',
  'Repairs',
  'Accessory Pay',
  'Detention',
  'Tarping',
  'Other Earning',
  'Other Deduction',
];

const EARNING_CATEGORIES = ['Accessory Pay', 'Detention', 'Tarping', 'Other Earning'];

export default function SettlementReviewModal({ settlement, onClose, onFinalized }) {
  const [lineItems, setLineItems] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [ytdData, setYtdData] = useState({ gross: 0, net: 0 });
  const [liveFuelAdvance, setLiveFuelAdvance] = useState(0);
  const [liveDocumented, setLiveDocumented] = useState(0);
  const [loadingAdvances, setLoadingAdvances] = useState(true);
  const [documents, setDocuments] = useState({ ebol: null, ratecon: null, bols: [] });
  const [weeklyInsurance, setWeeklyInsurance] = useState(0);
  const [weeklyEld, setWeeklyEld] = useState(0);
  const [weeklyTruckPayment, setWeeklyTruckPayment] = useState(0);
  const [weeklyTrailerPayment, setWeeklyTrailerPayment] = useState(0);

  // Fetch LIVE approved fuel advances for this load on modal open
  useEffect(() => {
    if (!settlement?.load_id) { setLoadingAdvances(false); return; }
    client.entities.FuelAdvance.filter({ load_id: settlement.load_id, status: 'Approved' })
      .then(records => {
        const total = records.reduce((sum, r) => sum + (r.amount || 0), 0);
        setLiveFuelAdvance(total);
        // Collect BOL docs from approved advances
        const bols = records.filter(r => r.bol_url).map(r => ({ label: `BOL — Fuel Advance $${r.amount}`, url: r.bol_url }));
        setDocuments(prev => ({ ...prev, bols }));
      })
      .finally(() => setLoadingAdvances(false));

    // Receipts (fuel/toll/scale/repair) the driver documented against this
    // load -- same offset ActiveLoad.jsx applies at delivery, re-checked
    // live here in case a driver added a receipt after delivering but
    // before this review, or an admin is re-reviewing.
    client.entities.Expense.filter({ load_id: settlement.load_id })
      .then(records => setLiveDocumented(records.reduce((sum, r) => sum + (r.amount || 0), 0)))
      .catch(() => setLiveDocumented(0));

    // Fetch load eBOL + Rate Con by load_id
    client.entities.Load.filter({ id: settlement.load_id }, '-created_date', 1)
      .then(loads => {
        const load = loads[0] || null;
        if (load) {
          setDocuments(prev => ({
            ...prev,
            ebol: load.ebol_url || null,
            ratecon: load.ratecon_url || null,
          }));
        }
      })
      .catch(() => {});
  }, [settlement?.load_id]);

  // Fetch company-wide weekly fixed deductions + driver's truck/trailer payments
  useEffect(() => {
    Promise.all([
      client.entities.CompanySettings.list(),
      settlement?.driver_email
        ? client.entities.DriverProfile.list('-created_date', 200)
        : Promise.resolve([]),
    ]).then(([settings, allProfiles]) => {
      const matchingProfiles = allProfiles.filter(p =>
        p.user_email?.toLowerCase() === settlement.driver_email?.toLowerCase()
      );
      const profile = matchingProfiles.find(p => p.approval_status === 'Approved') || matchingProfiles[0] || null;
      // Insurance/ELD are the driver's OWN truck insurance + ELD
      // subscription -- real costs only an Owner Operator carries.
      // A Company Driver runs carrier-owned, carrier-insured equipment,
      // so these never applied to them; they were being shown here
      // purely because these two settings are global CompanySettings
      // values with no driver_type gate at all (same bug Financials.jsx
      // had for the driver's own view of this).
      const isOwnerOperator = profile?.driver_type === 'Owner Operator';
      const ins = settings.find(s => s.key === 'insurance_deduction');
      const eld = settings.find(s => s.key === 'eld_weekly_cost');
      setWeeklyInsurance(isOwnerOperator ? parseFloat(ins?.value) || 0 : 0);
      setWeeklyEld(isOwnerOperator ? parseFloat(eld?.value) || 0 : 0);
      setWeeklyTruckPayment(profile?.weekly_truck_payment || 0);
      setWeeklyTrailerPayment(profile?.weekly_trailer_payment || 0);
    });
  }, [settlement?.id]);

  // Fetch YTD data for this driver (all finalized settlements this year)
  useEffect(() => {
    if (!settlement?.driver_email) return;
    fetchYTD();
  }, [settlement?.driver_email]);

  const fetchYTD = async () => {
    const currentYear = new Date().getFullYear();
    const allSettlements = await client.entities.Settlement.filter({
      driver_email: settlement.driver_email,
      is_finalized: true,
    }, '-finalized_date', 200);
    const thisYear = allSettlements.filter(s =>
      s.finalized_date && new Date(s.finalized_date).getFullYear() === currentYear
    );
    const gross = thisYear.reduce((sum, s) => sum + (s.gross_revenue || 0), 0);
    const net = thisYear.reduce((sum, s) => sum + (s.final_net_pay ?? s.net_driver_pay ?? 0), 0);
    setYtdData({ gross, net });
  };

  const addLineItem = () => {
    setLineItems(prev => [...prev, {
      id: Date.now(),
      category: 'Truck Payment',
      memo: '',
      amount: '',
      type: 'deduction',
    }]);
  };

  const updateLineItem = (id, field, value) => {
    setLineItems(prev => prev.map(item => {
      if (item.id !== id) return item;
      const updated = { ...item, [field]: value };
      // Auto-set type based on category
      if (field === 'category') {
        updated.type = EARNING_CATEGORIES.includes(value) ? 'earning' : 'deduction';
      }
      return updated;
    }));
  };

  const removeLineItem = (id) => {
    setLineItems(prev => prev.filter(item => item.id !== id));
  };

  // ── Core financials ── (fuelAdvance = live fetched, not stale snapshot)
  // NOTE: Insurance & ELD are WEEKLY fixed costs — applied once at payroll, NOT per-load
  const gross = settlement.gross_revenue || 0;
  const carrierFee = settlement.carrier_take_amount || gross * 0.15;
  const dispatchFee = settlement.dispatch_fee_amount || 0;
  // Displayed labels were hardcoded "(15%)"/"(3%)" regardless of what
  // Accounting had configured (or what this specific settlement actually
  // charged) in Pay & Deductions -- now reads the real percent this
  // settlement was created with, same fields markLoadDelivered stores.
  const carrierPercentDisplay = settlement.carrier_take_percent ?? 15;
  const dispatchPercentDisplay = settlement.dispatch_fee_percent ?? 3;
  // Documented receipts offset the raw advance dollar-for-dollar, capped
  // at the advance itself -- same math as ActiveLoad.jsx's
  // unaccountedAdvance, kept live here so a receipt added after delivery
  // still lands before payroll runs.
  const documentedCapped = Math.min(liveDocumented, liveFuelAdvance);
  const fuelAdvance = Math.max(0, liveFuelAdvance - liveDocumented); // always live, net of receipts
  const lumperFee = settlement.lumper_fee_deducted || 0;
  const lateFee = settlement.late_fee_deducted || 0;
  const baseNet = gross - carrierFee - dispatchFee - fuelAdvance - lumperFee - lateFee;

  // ── Manual adjustments ──
  const manualTotal = useMemo(() => {
    return lineItems.reduce((sum, item) => {
      const amt = parseFloat(item.amount) || 0;
      return sum + (item.type === 'earning' ? amt : -Math.abs(amt));
    }, 0);
  }, [lineItems]);

  const manualDeductions = lineItems
    .filter(i => i.type === 'deduction')
    .reduce((s, i) => s + (Math.abs(parseFloat(i.amount)) || 0), 0);
  const manualEarnings = lineItems
    .filter(i => i.type === 'earning')
    .reduce((s, i) => s + (Math.abs(parseFloat(i.amount)) || 0), 0);

  // Per-load deductions only (insurance & ELD excluded — weekly payroll only)
  const totalDeductions = carrierFee + dispatchFee + fuelAdvance + lumperFee + lateFee + manualDeductions;
  const liveNet = gross - totalDeductions + manualEarnings;

  const ytdGrossAfter = ytdData.gross + gross;
  const ytdNetAfter = ytdData.net + liveNet;

  const handleConfirm = async () => {
    setSubmitting(true);
    toast.info('Generating settlement statement...');

    // confirmedNet = per-load deductions only; insurance & ELD are weekly payroll deductions, not per-load
    const confirmedManualDeductions = lineItems
      .filter(i => i.type === 'deduction')
      .reduce((s, i) => s + (Math.abs(parseFloat(i.amount)) || 0), 0);
    const confirmedManualEarnings = lineItems
      .filter(i => i.type === 'earning')
      .reduce((s, i) => s + (Math.abs(parseFloat(i.amount)) || 0), 0);
    const confirmedNet = gross - carrierFee - dispatchFee - fuelAdvance - lumperFee - lateFee - confirmedManualDeductions + confirmedManualEarnings;

    const today = new Date().toISOString().split('T')[0];

    // 1. Save line items to entity
    const validItems = lineItems.filter(i => i.memo && parseFloat(i.amount));
    for (const item of validItems) {
      await client.entities.SettlementLineItem.create({
        settlement_id: settlement.id,
        driver_email: settlement.driver_email,
        driver_name: settlement.driver_name,
        category: item.category,
        memo: item.memo,
        amount: item.type === 'earning' ? Math.abs(parseFloat(item.amount)) : -Math.abs(parseFloat(item.amount)),
        type: item.type,
        settlement_date: today,
        year: new Date().getFullYear(),
      });
    }

    // 2. Mark settlement as Approved — save final_net_pay and manual adjustments as JSON
    const adjustmentsPayload = validItems.map(i => ({
      category: i.category,
      memo: i.memo,
      amount: i.type === 'earning' ? Math.abs(parseFloat(i.amount)) : -Math.abs(parseFloat(i.amount)),
      type: i.type,
    }));

    await client.entities.Settlement.update(settlement.id, {
      status: 'Approved',
      final_net_pay: confirmedNet,
      fuel_advance_total: fuelAdvance,
      fuel_advance_requested: liveFuelAdvance,
      fuel_advance_documented: documentedCapped,
      notes: adjustmentsPayload.length > 0 ? JSON.stringify(adjustmentsPayload) : undefined,
    });

    toast.success('Settlement approved! Add to batch when ready for payroll.');
    setSubmitting(false);
    onFinalized();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-background border border-border rounded-2xl w-full max-w-3xl my-8 z-10 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-border bg-white/3">
          <div>
            <h2 className="font-space font-bold text-foreground text-lg">Pre-Payroll Review</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {settlement.driver_name} — Load #{settlement.load_number} — {settlement.settlement_date}
            </p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6 max-h-[75vh] overflow-y-auto">
          {/* Settlement Totals */}
          <div className="space-y-3">
            {/* Income */}
            <div className="glass-card-dark rounded-xl overflow-hidden">
              <div className="px-4 py-2 bg-green-500/5 border-b border-green-500/20">
                <p className="text-xs font-semibold text-green-300 uppercase tracking-wide">Income</p>
              </div>
              <div className="divide-y divide-border">
                <Row label="Gross Revenue" value={`$${gross.toFixed(2)}`} />
                {manualEarnings > 0 && <Row label="Manual Earnings" value={`+$${manualEarnings.toFixed(2)}`} />}
              </div>
            </div>

            {/* Deductions (per-load + weekly fixed, combined) */}
            <div className="glass-card-dark rounded-xl overflow-hidden">
              <div className="px-4 py-2 bg-white/3 border-b border-border">
                <p className="text-xs font-semibold text-foreground uppercase tracking-wide">Deductions</p>
              </div>
              <div className="divide-y divide-border">
                <Row
                  label={
                    settlement.pay_model === 'mileage'
                      ? `Retained by carrier (driver paid $${(settlement.miles ? (gross - carrierFee) / settlement.miles : 0).toFixed(2)}/mi × ${settlement.miles || 0} mi)`
                      : settlement.pay_model === 'percentage'
                        ? `Retained by carrier (driver paid ${(100 - carrierPercentDisplay).toFixed(0)}% of gross)`
                        : `Carrier Take (${carrierPercentDisplay}%)`
                  }
                  value={`-$${carrierFee.toFixed(2)}`}
                  neg
                />
                {dispatchFee > 0 && <Row label={`Dispatch Fee (${dispatchPercentDisplay}%)`} value={`-$${dispatchFee.toFixed(2)}`} neg />}
                {liveFuelAdvance > 0 && <Row label="Approved Fuel Advances" value={`-$${liveFuelAdvance.toFixed(2)}`} neg />}
                {documentedCapped > 0 && <Row label="Receipts Documented" value={`+$${documentedCapped.toFixed(2)}`} pos />}
                {lumperFee > 0 && <Row label="Lumper Fee" value={`-$${lumperFee.toFixed(2)}`} neg />}
                {lateFee > 0 && <Row label="Late Fee" value={`-$${lateFee.toFixed(2)}`} neg />}
                {manualDeductions > 0 && <Row label="Manual Deductions" value={`-$${manualDeductions.toFixed(2)}`} neg />}
              </div>
              {(weeklyInsurance > 0 || weeklyEld > 0 || weeklyTruckPayment > 0 || weeklyTrailerPayment > 0) && (
                <>
                  <div className="px-4 py-1.5 bg-blue-500/5 border-y border-blue-500/20">
                    <p className="text-[10px] font-semibold text-blue-300 uppercase tracking-wide">Weekly Fixed — not deducted from this load, applied once per week at payroll</p>
                  </div>
                  <div className="divide-y divide-border">
                    {weeklyInsurance > 0 && <Row label="Insurance (weekly)" value={`-$${weeklyInsurance.toFixed(2)}`} neg />}
                    {weeklyEld > 0 && <Row label="ELD (weekly)" value={`-$${weeklyEld.toFixed(2)}`} neg />}
                    {weeklyTruckPayment > 0 && <Row label="Truck Payment (weekly)" value={`-$${weeklyTruckPayment.toFixed(2)}`} neg />}
                    {weeklyTrailerPayment > 0 && <Row label="Trailer Payment (weekly)" value={`-$${weeklyTrailerPayment.toFixed(2)}`} neg />}
                  </div>
                </>
              )}
              <div className="px-4 py-3 bg-green-500/5 border-t border-green-500/20 flex justify-between items-center">
                <span className="font-bold text-foreground text-sm">Net Pay (This Load)</span>
                <span className={cn("font-bold text-lg", liveNet >= 0 ? 'text-green-400' : 'text-red-400')}>
                  ${liveNet.toFixed(2)}
                </span>
              </div>
            </div>

            {/* Manual Line Items */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Manual Adjustments</h3>
                <Button size="sm" onClick={addLineItem} variant="outline" className="border-border hover:bg-white/5 h-7 px-3 text-xs">
                  <Plus className="w-3 h-3 mr-1" /> Add Line Item
                </Button>
              </div>

              {lineItems.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-4 border border-dashed border-border rounded-xl">
                  No manual adjustments. Click "Add Line Item" to add deductions or earnings.
                </p>
              )}

              <div className="space-y-2">
                {lineItems.map(item => (
                  <div key={item.id} className="glass-card-dark rounded-xl p-3 grid grid-cols-[1fr_1fr_auto_auto] gap-2 items-center">
                    {/* Category */}
                    <select
                      value={item.category}
                      onChange={e => updateLineItem(item.id, 'category', e.target.value)}
                      className="px-2 py-1.5 rounded-lg bg-transparent border border-input text-foreground text-xs focus:outline-none"
                    >
                      {CATEGORIES.map(c => (
                        <option key={c} value={c} className="bg-background">{c}</option>
                      ))}
                    </select>

                    {/* Memo */}
                    <input
                      value={item.memo}
                      onChange={e => updateLineItem(item.id, 'memo', e.target.value)}
                      placeholder="Memo / description…"
                      className="px-2 py-1.5 rounded-lg bg-transparent border border-input text-foreground text-xs placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/40"
                    />

                    {/* Amount with type toggle */}
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => updateLineItem(item.id, 'type', item.type === 'deduction' ? 'earning' : 'deduction')}
                        className={cn(
                          "text-xs font-bold w-6 h-7 rounded-l-lg border flex items-center justify-center transition-colors",
                          item.type === 'deduction'
                            ? 'bg-red-500/20 border-red-500/40 text-red-400'
                            : 'bg-green-500/20 border-green-500/40 text-green-400'
                        )}
                        title="Toggle earning/deduction"
                      >
                        {item.type === 'deduction' ? '−' : '+'}
                      </button>
                      <input
                        type="number"
                        value={item.amount}
                        onChange={e => updateLineItem(item.id, 'amount', e.target.value)}
                        placeholder="0.00"
                        min="0"
                        className="w-24 px-2 py-1.5 rounded-r-lg bg-transparent border border-input text-foreground text-xs placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/40"
                      />
                    </div>

                    <button
                      onClick={() => removeLineItem(item.id)}
                      className="text-muted-foreground hover:text-red-400 transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

          </div>

          {/* Documents */}
          {(documents.ratecon || documents.ebol || documents.bols.length > 0) && (
            <div>
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Documents</h3>
              <div className="glass-card-dark rounded-xl divide-y divide-border overflow-hidden">
                {documents.ratecon && (
                  <DocRow icon="green" label="Rate Confirmation" url={documents.ratecon} />
                )}
                {documents.ebol && (
                  <DocRow icon="blue" label="eBOL (Bill of Lading)" url={documents.ebol} />
                )}
                {documents.bols.map((doc, i) => (
                  <DocRow key={i} icon="amber" label={doc.label} url={doc.url} />
                ))}
              </div>
            </div>
          )}

          {/* YTD Preview */}
          <div className="glass-card-dark rounded-xl p-4">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">YTD After This Statement</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-muted-foreground">Gross YTD</p>
                <p className="text-lg font-bold text-foreground">${ytdGrossAfter.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Net YTD</p>
                <p className={cn("text-lg font-bold", ytdNetAfter >= 0 ? 'text-green-400' : 'text-red-400')}>
                  ${ytdNetAfter.toFixed(2)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border bg-white/3 flex gap-3 justify-end">
          <Button onClick={onClose} variant="outline" className="border-border" disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={submitting}
            className="bg-primary text-primary-foreground min-w-[180px]"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Approving...</>
            ) : (
              <><Lock className="w-4 h-4 mr-2" /> Approve Settlement</>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, neg, pos, bold }) {
  return (
    <div className={cn("flex justify-between items-center px-4 py-2 text-sm", bold ? "font-bold" : "")}>
      <span className={bold ? "text-foreground" : "text-muted-foreground"}>{label}</span>
      <span className={cn(
        bold ? "text-foreground" : "",
        neg ? "text-red-400" : pos ? "text-green-400" : !bold ? "text-foreground" : ""
      )}>{value}</span>
    </div>
  );
}

function DocRow({ icon, label, url }) {
  const iconColors = { green: 'text-green-400', blue: 'text-blue-400', amber: 'text-amber-400' };
  return (
    <div className="flex items-center justify-between px-4 py-3">
      <div className="flex items-center gap-2">
        <FileText className={`w-4 h-4 ${iconColors[icon] || 'text-muted-foreground'}`} />
        <span className="text-sm text-foreground">{label}</span>
      </div>
      <div className="flex items-center gap-2">
        <a href={url} target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 transition-colors px-2 py-1 rounded-lg hover:bg-blue-500/10">
          <ExternalLink className="w-3 h-3" /> View
        </a>
        <a href={url} download
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1 rounded-lg hover:bg-white/5">
          <Download className="w-3 h-3" /> Download
        </a>
      </div>
    </div>
  );
}

// ─── Legacy — kept for reference, no longer used ─────────────────────────────
function buildSettlementHTML_unused({ settlement, loadData, carrierFee, dispatchFee, fuelAdvance, liveNet, totalDeductions, earningItems, deductionItems, ytdGrossAfter, ytdNetAfter, today, paymentRef, driverCompanyName }) {
  const gross = settlement.gross_revenue || 0;
  const driverPay85 = gross * 0.85;

  // Build route string: prefer pickup/dropoff addresses, fall back to origin/destination
  const fromCity = loadData?.pickup_address || loadData?.origin || '';
  const toCity = loadData?.dropoff_address || loadData?.destination || '';
  const routeString = fromCity && toCity ? `${fromCity} to ${toCity}` : (fromCity || toCity || '—');

  const fmt = (n) => `$${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtNeg = (n) => `-$${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const baseStyle = `
    font-family: Arial, sans-serif;
    font-size: 13px;
    color: #000;
    background: white;
    padding: 48px 56px;
    width: 794px;
    box-sizing: border-box;
  `;

  const tableStyle = `width:100%;border-collapse:collapse;margin-bottom:16px;`;
  const thStyle = `border:1px solid #ccc;padding:7px 10px;text-align:left;font-weight:bold;background:#f5f5f5;font-size:12px;`;
  const tdStyle = `border:1px solid #ccc;padding:7px 10px;font-size:12px;`;
  const tdRightStyle = `border:1px solid #ccc;padding:7px 10px;font-size:12px;text-align:right;`;

  // Page 1 — Summary
  const payToLine = driverCompanyName
    ? `${driverCompanyName} (${settlement.driver_name})`
    : settlement.driver_name;

  const page1 = `
    <div class="pdf-page" style="${baseStyle}">
      <h1 style="font-size:26px;font-weight:normal;margin:0 0 20px 0;">Driver Settlement For ${settlement.driver_name}</h1>
      <table style="margin-bottom:20px;font-size:13px;border:none;">
        <tr><td style="font-weight:bold;padding:2px 16px 2px 0;border:none;">Company</td><td style="border:none;">Carrier Portal</td></tr>
        <tr><td style="font-weight:bold;padding:2px 16px 2px 0;border:none;">Address</td><td style="border:none;">Las Vegas, NV</td></tr>
        <tr><td style="font-weight:bold;padding:2px 16px 2px 0;border:none;">Pay To</td><td style="border:none;font-weight:bold;">${payToLine}</td></tr>
        <tr><td style="font-weight:bold;padding:2px 16px 2px 0;border:none;">Driver</td><td style="border:none;">${settlement.driver_name}</td></tr>
        <tr><td style="font-weight:bold;padding:2px 16px 2px 0;border:none;">Email</td><td style="border:none;">${settlement.driver_email}</td></tr>
      </table>
      <h2 style="font-size:20px;font-weight:normal;border-bottom:1px solid #ccc;padding-bottom:8px;margin-bottom:16px;">
        Paid ${fmt(liveNet)} On ${today}${paymentRef ? ` — Ref: ${paymentRef}` : ''}
      </h2>
      <table style="${tableStyle}">
        <tr><td style="${tdStyle}"><strong>Gross Earnings</strong></td><td style="${tdRightStyle}">${fmt(gross)}</td></tr>
        <tr><td style="${tdStyle}">Deductions</td><td style="${tdRightStyle}">${fmtNeg(totalDeductions)}</td></tr>
        <tr><td style="${tdStyle}">Net Earnings</td><td style="${tdRightStyle}">${fmt(liveNet)}</td></tr>
        <tr><td style="${tdStyle}">Pay Advances</td><td style="${tdRightStyle}">${fmt(fuelAdvance)}</td></tr>
        <tr><td style="${tdStyle}">Net Pay</td><td style="${tdRightStyle}"><strong>${fmt(liveNet)}</strong></td></tr>
      </table>
    </div>`;

  // Page 2 — Earnings
  const accessoryRows = earningItems.map(item => `
    <tr>
      <td colspan="3" style="${tdStyle}">${item.category}</td>
      <td colspan="1" style="${tdStyle}"></td>
      <td style="${tdRightStyle}">$${Math.abs(parseFloat(item.amount)).toFixed(2)}</td>
      <td style="${tdRightStyle}">$${Math.abs(parseFloat(item.amount)).toFixed(2)}</td>
    </tr>`).join('');

  const page2 = `
    <div class="pdf-page" style="${baseStyle}">
      <h2 style="font-size:22px;font-weight:normal;margin:0 0 20px 0;">Earnings</h2>
      <table style="${tableStyle}">
        <thead>
          <tr>
            <th style="${thStyle}">Trip</th>
            <th style="${thStyle}">Date</th>
            <th style="${thStyle}">From/To</th>
            <th style="${thStyle}">Details</th>
            <th style="${thStyle}">Gross Revenue</th>
            <th style="${thStyle}">Driver Pay</th>
            <th style="${thStyle}">Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style="${tdStyle}">${settlement.load_number}</td>
            <td style="${tdStyle}">${settlement.settlement_date || today}</td>
            <td style="${tdStyle}">${routeString}</td>
            <td style="${tdStyle}">Base Pay</td>
            <td style="${tdRightStyle}">85.0%</td>
            <td style="${tdRightStyle}">${fmt(gross)}</td>
            <td style="${tdRightStyle}">${fmt(driverPay85)}</td>
          </tr>
          <tr>
            <td colspan="4" style="${tdStyle}">Accessory Pay</td>
            <td colspan="2" style="${tdStyle}"></td>
            <td style="${tdRightStyle}">$0.00</td>
          </tr>
          ${accessoryRows}
        </tbody>
      </table>
      <div style="text-align:right;font-size:13px;margin-top:8px;">
        <div>Gross Earnings: <strong>${fmt(gross)}</strong></div>
        <div>Gross Revenue: <strong>${fmt(gross)}</strong></div>
      </div>
    </div>`;

  // Page 3 — Deductions + YTD
  const standardDeductions = [];
  if (carrierFee > 0) standardDeductions.push({ category: 'Carrier Fee (15%)', memo: '15% of gross rate per contract', amount: carrierFee });
  if (dispatchFee > 0) standardDeductions.push({ category: 'Dispatch Fee (3%)', memo: '3% dispatch service fee', amount: dispatchFee });
  if (fuelAdvance > 0) standardDeductions.push({ category: 'Fuel Advance', memo: 'Fuel advance deduction', amount: fuelAdvance });

  const allDeductionRows = [
    ...standardDeductions.map(d => `
      <tr>
        <td style="${tdStyle}">${d.category}</td>
        <td style="${tdStyle}">${d.memo}</td>
        <td style="${tdRightStyle}">${fmtNeg(d.amount)}</td>
      </tr>`),
    ...deductionItems.map(item => `
      <tr>
        <td style="${tdStyle}">${item.category}</td>
        <td style="${tdStyle}">${item.memo}</td>
        <td style="${tdRightStyle}">${fmtNeg(Math.abs(parseFloat(item.amount)))}</td>
      </tr>`),
  ].join('');

  // YTD deductions grouped
  const ytdTotalDeductions = ytdGrossAfter - ytdNetAfter;

  const page3 = `
    <div class="pdf-page" style="${baseStyle}">
      <h2 style="font-size:22px;font-weight:normal;margin:0 0 16px 0;">Deductions</h2>
      <table style="${tableStyle}">
        <thead>
          <tr>
            <th style="${thStyle}">Category</th>
            <th style="${thStyle}">Memo</th>
            <th style="${thStyle};text-align:right;">Amount</th>
          </tr>
        </thead>
        <tbody>
          ${allDeductionRows}
          <tr>
            <td colspan="2" style="${tdStyle}"><strong>Subtotal</strong></td>
            <td style="${tdRightStyle}"><strong>${fmtNeg(totalDeductions)}</strong></td>
          </tr>
        </tbody>
      </table>

      <h2 style="font-size:22px;font-weight:normal;margin:24px 0 12px 0;">Year To Date</h2>
      <table style="${tableStyle}">
        <tr>
          <td style="${tdStyle}">Gross YTD Earnings</td>
          <td style="${tdRightStyle}">${fmt(ytdGrossAfter)}</td>
          <td style="${tdStyle}"></td>
        </tr>
        <tr>
          <td colspan="3" style="${thStyle};text-align:center;">Deductions</td>
        </tr>
        <tr>
          <td style="${tdStyle}">Total Deductions YTD</td>
          <td style="${tdStyle}"></td>
          <td style="${tdRightStyle}">${fmtNeg(ytdTotalDeductions)}</td>
        </tr>
        <tr>
          <td style="${tdStyle}"><strong>Totals</strong></td>
          <td style="${tdRightStyle}"><strong>${fmt(ytdGrossAfter)}</strong></td>
          <td style="${tdRightStyle}"><strong>${fmtNeg(ytdTotalDeductions)}</strong></td>
        </tr>
        <tr style="background:#f5f5f5;">
          <td colspan="2" style="${tdStyle}"><strong>Net YTD Earnings</strong></td>
          <td style="${tdRightStyle}"><strong>${fmt(ytdNetAfter)}</strong></td>
        </tr>
      </table>

      <div style="margin-top:32px;border-top:1px solid #eee;padding-top:16px;">
        <p style="font-weight:bold;margin:0 0 6px 0;font-size:13px;">Notes</p>
        <p style="font-size:12px;color:#555;margin:0;">Generated By Carrier Portal on ${today}.</p>
      </div>
    </div>`;

  return page1 + page2 + page3;
}